import { useState, useEffect, useRef } from 'react';

/**
 * Manages the Web Speech API recognition lifecycle.
 * Calls onFinalTranscript(text) when a complete utterance is detected.
 *
 * @param {object} opts
 * @param {Function} opts.onFinalTranscript - called with the final transcript string
 * @param {boolean}  opts.isProcessing      - blocks new recording while true
 * @param {boolean}  opts.isSpeaking        - tapping mic while speaking cancels TTS instead
 * @param {Function} opts.cancelSpeech      - called to stop active TTS
 * @param {Function} opts.log               - (type, msg) => void
 */
export function useVoiceInput({ onFinalTranscript, isProcessing, isSpeaking, cancelSpeech, log }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript,  setTranscript]  = useState('');
  const [supported,   setSupported]   = useState(true);

  const recognitionRef      = useRef(null);
  const interimLogTimerRef  = useRef(null);
  const onFinalRef          = useRef(onFinalTranscript);
  onFinalRef.current = onFinalTranscript;

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      log('error', 'SpeechRecognition not supported in this browser');
      return;
    }

    const rec = new SR();
    rec.continuous     = false;
    rec.interimResults = true;
    rec.lang           = 'en-US';

    rec.onstart      = () => { log('status', 'Microphone opened — listening'); };
    rec.onaudiostart = () => { log('status', 'Audio capture started'); };

    rec.onresult = (e) => {
      const text    = Array.from(e.results).map(r => r[0].transcript).join('');
      const isFinal = e.results[e.results.length - 1].isFinal;
      setTranscript(text);
      if (isFinal) {
        if (interimLogTimerRef.current) { clearTimeout(interimLogTimerRef.current); interimLogTimerRef.current = null; }
        log('user', `"${text}"`);
        rec.stop();
        onFinalRef.current?.(text);
      } else {
        if (interimLogTimerRef.current) clearTimeout(interimLogTimerRef.current);
        interimLogTimerRef.current = setTimeout(() => {
          log('status', `Hearing: "${text}"`);
          interimLogTimerRef.current = null;
        }, 1500);
      }
    };

    rec.onerror = (e) => {
      log('error', `Speech recognition error: ${e.error}`);
      setIsListening(false);
      setTranscript('');
    };
    rec.onend = () => {
      setIsListening(false);
      log('status', 'Microphone closed');
    };

    recognitionRef.current = rec;
    log('system', 'SpeechRecognition ready (lang: en-US)');

    return () => {
      try { rec.abort(); } catch {}
      if (interimLogTimerRef.current) clearTimeout(interimLogTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleListening = () => {
    if (isProcessing) { log('status', 'Blocked — waiting for API response'); return; }
    if (isSpeaking)   { cancelSpeech(); return; }
    if (isListening) {
      log('user', 'User stopped recording');
      recognitionRef.current?.stop();
    } else {
      window.speechSynthesis?.cancel();
      setTranscript('');
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        log('status', 'Recording started — tap mic to stop');
      } catch (e) {
        log('error', `Could not start microphone: ${e.message}`);
      }
    }
  };

  return { isListening, transcript, setTranscript, supported, toggleListening };
}
